import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  FileCode,
  ChevronDown,
  ChevronRight,
  Copy,
  Download,
  Activity,
  XCircle,
  TrendingUp,
  Calendar,
  FileWarning,
  GitCompare,
  Eye,
  Play,
  Edit,
  Save,
  X,
  GitPullRequest,
  ExternalLink,
  Bug,
  ClipboardList,
  History,
  RotateCcw,
  Network,
  Globe,
  MessageSquare,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Separator } from '@/components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { apiClient, IncidentDetails, Cluster, TopologyIncident } from '../api/client';
import { cn } from '@/lib/utils';
import { getStatusLabel } from '../lib/incidents';
import { parseFormattedText } from '@/lib/textFormatting';
import * as Diff from 'diff';
import { ModeToggle } from '@/components/mode-toggle';
import { DeployToGitHubButton } from '@/components/github';
import { EvidenceMetricsCarousel } from '@/components/EvidenceMetricsCarousel';
import { MobileMenuButton } from '@/components/MobileMenuButton';
import { IncidentChat } from '@/components/IncidentChat';
import {
  ReactFlow,
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  Node,
  Edge,
  Handle,
  Position,
  MarkerType,
  ReactFlowProvider,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { getWorkloadIcon } from './TopologyMap/NodeComponents';

// Format timestamp to full datetime in UTC
function formatDateTime(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short',
  });
}

// Format timestamp to time only in UTC
function formatTime(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}

// Get severity icon

// Get initials from email
function getInitials(email: string): string {
  if (!email) return '?';
  const parts = email.split('@')[0].split('.');
  if (parts.length >= 2) {
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }
  return email.split('@')[0].charAt(0).toUpperCase();
}

// Get event type icon
function getEventTypeIcon(eventType: string, severity: string) {
  switch (eventType) {
    case 'alert':
      return <AlertTriangle className="h-4 w-4 text-red-500" />;
    case 'pagerduty_alert':
      return <AlertTriangle className="h-4 w-4 text-muted-foreground" />;
    case 'log_entry':
      return severity === 'ERROR' || severity === 'FATAL' ? (
        <XCircle className="h-4 w-4 text-red-500" />
      ) : (
        <FileWarning className="h-4 w-4 text-orange-500" />
      );
    case 'status_change':
      return <Activity className="h-4 w-4 text-blue-500" />;
    case 'deployment':
      return <TrendingUp className="h-4 w-4 text-purple-500" />;
    default:
      return <Clock className="h-4 w-4 text-gray-500" />;
  }
}

// Get incident type label
function getIncidentTypeLabel(type: string): string {
  switch (type) {
    // Infrastructure incidents
    case 'OOMKilled':
      return 'Out of Memory';
    case 'CrashLoopBackOff':
      return 'Crash Loop BackOff';
    case 'ImagePullError':
      return 'Image Pull Error';
    case 'RolloutFailure':
      return 'Rollout Failure';
    case 'NodeNotReady':
      return 'Node Not Ready';
    case 'MissingPVC':
      return 'Missing PersistentVolumeClaim';
    case 'InsufficientResources':
      return 'Insufficient Cluster Resources';
    case 'SchedulingFailure':
      return 'Pod Scheduling Failure';
    case 'TaintTolerationMismatch':
      return 'Taint/Toleration Mismatch';
    case 'NodeSelectorMismatch':
      return 'Node Selector Mismatch';
    case 'PodEvicted':
      return 'Pod Evicted';
    case 'LivenessProbeFailure':
      return 'Liveness Probe Failure';
    case 'ReadinessProbeFailure':
      return 'Readiness Probe Failure';
    case 'HealthCheckFailure':
      return 'Health Check Failure';
    case 'VolumeError':
      return 'Volume Error';
    case 'VolumeResizeError':
      return 'Volume Resize Error';
    case 'NetworkError':
      return 'Network Error';
    case 'RBACError':
      return 'RBAC Error';
    case 'ResourceExhaustion':
      return 'Resource Exhaustion';
    case 'ContainerRuntimeError':
      return 'Container Runtime Error';
    case 'LifecycleHookError':
      return 'Lifecycle Hook Error';
    case 'ConfigError':
      return 'Configuration Error';
    case 'PodFailure':
      return 'Pod Failure';
    case 'NodeIssue':
      return 'Node Issue';
    case 'Unknown':
      return 'Unknown Incident';
    default:
      return type;
  }
}

// Get icon component for timeline events
function getTimelineIcon(iconType: string) {
  // Monochrome: black in light mode, white in dark mode (theme foreground).
  const cls = 'h-4 w-4 text-foreground';
  switch (iconType) {
    case 'alert':
      return <AlertTriangle className={cls} />;
    case 'log':
      return <FileWarning className={cls} />;
    case 'error':
      return <XCircle className={cls} />;
    case 'status':
      return <Activity className={cls} />;
    case 'metric':
      return <TrendingUp className={cls} />;
    case 'deployment':
      return <GitCompare className={cls} />;
    default:
      return <Clock className={cls} />;
  }
}


// Create GitHub-style diff using Myers algorithm
const createDiff = (oldText: string, newText: string): Array<{line: string, type: 'unchanged' | 'removed' | 'added', lineNumber?: number}> => {
  const changes = Diff.diffLines(oldText, newText);
  const result: Array<{line: string, type: 'unchanged' | 'removed' | 'added', lineNumber?: number}> = [];
  
  let oldLineNumber = 1;
  let newLineNumber = 1;
  
  changes.forEach(change => {
    const lines = change.value.split('\n');
    // Remove the last empty line if it exists (common with diffLines)
    if (lines[lines.length - 1] === '') {
      lines.pop();
    }
    
    lines.forEach(line => {
      if (change.removed) {
        result.push({ 
          line, 
          type: 'removed',
          lineNumber: oldLineNumber++
        });
      } else if (change.added) {
        result.push({ 
          line, 
          type: 'added',
          lineNumber: newLineNumber++
        });
      } else {
        result.push({ 
          line, 
          type: 'unchanged',
          lineNumber: oldLineNumber
        });
        oldLineNumber++;
        newLineNumber++;
      }
    });
  });
  
  return result;
};

// YAML Fix Component
interface YAMLFixCardProps {
  fix: {
    file_path: string;
    file_type: string;
    yaml_content: string;
    existing_yaml?: string;
    explanation: string;
    resource_name?: string;
    resource_kind?: string;
    resource_namespace?: string;
  };
  index: number;
  incidentId: string;
  clusterId: string;
  cluster?: Cluster;
}

function YAMLFixCard({ fix, index, incidentId, clusterId, cluster }: YAMLFixCardProps) {
  const [isOpen, setIsOpen] = useState(index === 0); // First fix open by default
  const [copied, setCopied] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);

  // Check if safe-apply is enabled for this cluster
  const canSafeApply = cluster?.safe_apply === true;

  const handleCopy = () => {
    navigator.clipboard.writeText(fix.yaml_content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([fix.yaml_content], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fix.file_path.split('/').pop() || 'fix.yaml';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleApply = async () => {
    setApplying(true);
    setApplyError(null);
    
    try {
      const result = await apiClient.applyIncidentYAMLFix(incidentId, index);
      
      if (result.success) {
        setApplied(true);
        // Reload page after 2 seconds to show updated status and Validate Fix button
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        setApplyError(result.error_message || 'Failed to apply YAML fix');
      }
    } catch (err: any) {
      console.error('Failed to apply YAML fix:', err);
      setApplyError(err.response?.data?.error_message || err.message || 'Failed to apply YAML fix');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="border rounded-lg">
        <CollapsibleTrigger className="w-full hover:bg-muted/50 transition-colors rounded-t-lg">
          <div className="flex items-center justify-between p-4 min-w-0">
            <div className="flex items-center gap-3 min-w-0">
              {isOpen ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
              <div className="flex items-center gap-2 min-w-0">
                <Badge variant="outline" className="font-mono text-xs flex-shrink-0">
                  {fix.file_type}
                </Badge>
                <span className="font-medium truncate">{fix.file_path}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {fix.resource_kind && (
                <Badge variant="secondary" className="text-xs">
                  {fix.resource_kind}
                </Badge>
              )}
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-4 pb-4 space-y-4">
            <Separator />

            {/* Explanation */}
            <div>
              <h4 className="text-sm font-semibold mb-2">Explanation</h4>
              <p className="text-sm text-muted-foreground">{parseFormattedText(fix.explanation)}</p>
            </div>

            {/* Resource Info */}
            {(fix.resource_name || fix.resource_namespace) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                {fix.resource_namespace && (
                  <div>
                    <span className="text-muted-foreground">Namespace:</span>
                    <span className="ml-2 font-mono">{fix.resource_namespace}</span>
                  </div>
                )}
                {fix.resource_name && (
                  <div>
                    <span className="text-muted-foreground">Resource:</span>
                    <span className="ml-2 font-mono">{fix.resource_name}</span>
                  </div>
                )}
              </div>
            )}

            {/* Application Result Alert */}
            {applyError && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{applyError}</AlertDescription>
              </Alert>
            )}

            {applied && (
              <Alert className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                <AlertDescription className="text-green-800 dark:text-green-200">
                  YAML fix applied successfully!
                </AlertDescription>
              </Alert>
            )}

            {/* Safe-Apply Disabled Warning */}
            {!canSafeApply && cluster && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Safe-apply is disabled</strong> for this cluster. The Apply button is unavailable. Use the GitHub integration or download and apply this fix manually.
                </AlertDescription>
              </Alert>
            )}

            {/* YAML Content with Diff Toggle */}
            <div>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <h4 className="text-sm font-semibold">YAML Configuration</h4>
                <div className="flex items-center gap-2 flex-wrap">
                  {canSafeApply ? (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleApply();
                      }}
                      disabled={applying || applied}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {applying ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Applying...
                        </>
                      ) : applied ? (
                        <>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Applied
                        </>
                      ) : (
                        <>
                          <Play className="h-4 w-4 mr-2" />
                          Apply
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled
                      className="text-xs"
                      title="Safe-apply is disabled for this cluster"
                    >
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      Safe-Apply Disabled
                    </Button>
                  )}
                  <DeployToGitHubButton
                    yamlContent={fix.yaml_content}
                    sourceType="incident_yaml_fix"
                    sourceId={`${incidentId}_fix_${index}`}
                    defaultTitle={`[KestrelAI] Incident Fix: ${fix.file_type} ${fix.resource_kind}/${fix.resource_name || 'resource'}`}
                    defaultDescription={`Incident remediation YAML${fix.explanation ? `: ${fix.explanation}` : ''}`}
                    className="text-xs"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopy();
                    }}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload();
                    }}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </div>
              </div>

              {/* Toggle between Updated YAML and Diff for updates */}
              {fix.existing_yaml && fix.file_type === 'update' && (
                <div className="flex justify-center mb-3">
                  <div className="flex items-center gap-2 bg-muted p-1 rounded-md">
                    <Button
                      variant={!showDiff ? "default" : "ghost"}
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDiff(false);
                      }}
                      className="text-xs h-6"
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      Updated YAML
                    </Button>
                    <Button
                      variant={showDiff ? "default" : "ghost"}
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDiff(true);
                      }}
                      className="text-xs h-6"
                    >
                      <GitCompare className="h-3 w-3 mr-1" />
                      Show Diff
                    </Button>
                  </div>
                </div>
              )}

              {/* YAML Content Display */}
              {fix.existing_yaml && fix.file_type === 'update' && showDiff ? (
                // Unified diff view for updates
                <div className="bg-muted p-3 rounded-md border font-mono text-xs overflow-x-auto max-h-96">
                  {createDiff(fix.existing_yaml, fix.yaml_content).map((diffLine, lineIndex) => (
                    <div 
                      key={lineIndex} 
                      className={cn(
                        "whitespace-pre-wrap flex",
                        diffLine.type === 'removed' && "bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200",
                        diffLine.type === 'added' && "bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200",
                        diffLine.type === 'unchanged' && "text-foreground hover:bg-muted/50"
                      )}
                    >
                      <span className={cn(
                        "inline-block w-6 text-right mr-2 text-muted-foreground select-none flex-shrink-0 border-r border-border/50 pr-1",
                        diffLine.type === 'removed' && "text-red-600 dark:text-red-400 bg-red-100/50 dark:bg-red-900/30",
                        diffLine.type === 'added' && "text-green-600 dark:text-green-400 bg-green-100/50 dark:bg-green-900/30"
                      )}>
                        {diffLine.type === 'removed' ? '-' : diffLine.type === 'added' ? '+' : ' '}
                      </span>
                      <span className="flex-1 min-w-0">
                        {diffLine.line}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                // Single view (for creates, deletes, or when not in diff mode)
                <pre className={cn(
                  "p-4 rounded-md border font-mono text-xs overflow-x-auto max-h-96",
                  fix.file_type === 'delete' 
                    ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800"
                    : fix.file_type === 'create'
                    ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
                    : "bg-muted"
                )}>
                  <code className="text-foreground">
                    {fix.file_type === 'delete' && fix.existing_yaml 
                      ? fix.existing_yaml 
                      : fix.yaml_content}
                  </code>
                </pre>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

// Code Fix Component for Application-Level Incidents
interface CodeFixCardProps {
  fix: {
    file_path: string;
    file_type: string;
    original_code?: string;
    fixed_code: string;
    explanation: string;
    line_start: number;
    line_end: number;
    language: string;
    repo_id?: string;
  };
  index: number;
  incidentId: string;
  prUrl?: string;
}

function CodeFixCard({ fix, index, incidentId, prUrl: externalPrUrl }: CodeFixCardProps) {
  const [isOpen, setIsOpen] = useState(index === 0); // First fix open by default
  const [copied, setCopied] = useState(false);
  const [showDiff, setShowDiff] = useState(!!fix.original_code); // Only default to diff view when original_code exists
  const [prUrl, setPrUrl] = useState<string | null>(externalPrUrl || null);

  // Sync prUrl when parent updates it (e.g., bulk PR creation)
  useEffect(() => {
    if (externalPrUrl) setPrUrl(externalPrUrl);
  }, [externalPrUrl]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fix.fixed_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable (non-HTTPS, iframe, etc.)
    }
  };

  const handleDownload = () => {
    const filePath = fix.file_path || '';
    const blob = new Blob([fix.fixed_code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filePath.split('/').pop() || 'code.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Get language badge color
  const getLanguageColor = (lang: string) => {
    switch (lang.toLowerCase()) {
      case 'python': return 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400';
      case 'go': return 'bg-cyan-500/20 text-cyan-700 dark:text-cyan-400';
      case 'javascript': return 'bg-amber-500/20 text-amber-700 dark:text-amber-400';
      case 'typescript': return 'bg-blue-500/20 text-blue-700 dark:text-blue-400';
      case 'java': return 'bg-red-500/20 text-red-700 dark:text-red-400';
      case 'rust': return 'bg-orange-500/20 text-orange-700 dark:text-orange-400';
      default: return 'bg-gray-500/20 text-gray-700 dark:text-gray-400';
    }
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="border rounded-lg overflow-hidden">
        <CollapsibleTrigger className="w-full hover:bg-muted/50 transition-colors rounded-t-lg">
          <div className="flex items-center justify-between p-4 gap-2">
            <div className="flex items-center gap-3 min-w-0">
              {isOpen ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              )}
              <div className="flex items-center gap-2 min-w-0">
                <Badge className={cn("font-mono text-xs flex-shrink-0", getLanguageColor(fix.language))}>
                  {fix.language}
                </Badge>
                <span className="font-medium truncate">{fix.file_path}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Badge variant="outline" className="text-xs whitespace-nowrap">
                Lines {fix.line_start}-{fix.line_end}
              </Badge>
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="p-4 pt-0 space-y-4 overflow-hidden">
            {/* Explanation */}
            <div className="text-sm text-muted-foreground bg-muted p-3 rounded-md border">
              <strong className="text-foreground">Fix Explanation:</strong>
              <div className="mt-1">{parseFormattedText(fix.explanation)}</div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-wrap">
              {fix.original_code && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDiff(!showDiff);
                  }}
                  className="flex items-center gap-1"
                >
                  <Eye className="h-3 w-3" />
                  {showDiff ? 'Show Fixed Code' : 'Show Diff'}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy();
                }}
                className="flex items-center gap-1"
              >
                <Copy className="h-3 w-3" />
                {copied ? 'Copied!' : 'Copy'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload();
                }}
                className="flex items-center gap-1"
              >
                <Download className="h-3 w-3" />
                Download
              </Button>

              {/* Show View PR link if bulk PR was created */}
              {prUrl && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(prUrl, '_blank');
                  }}
                  className="flex items-center gap-1"
                >
                  <ExternalLink className="h-3 w-3" />
                  View Pull Request
                </Button>
              )}
            </div>

            {/* Code Content */}
            <div className="overflow-hidden">
              {showDiff && fix.original_code ? (
                <div className="rounded-md border overflow-hidden max-w-full">
                  <div className="bg-muted px-4 py-2 text-xs font-medium flex items-center justify-between border-b">
                    <span>Changes</span>
                    <span className="text-muted-foreground">Lines {fix.line_start}-{fix.line_end}</span>
                  </div>
                  <div className="font-mono text-xs overflow-x-auto max-h-96">
                    {createDiff(fix.original_code, fix.fixed_code).map((line, idx) => (
                      <div
                        key={idx}
                        className={cn(
                          "px-4 py-0.5 whitespace-pre",
                          line.type === 'removed' && "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200",
                          line.type === 'added' && "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200",
                          line.type === 'unchanged' && "bg-background"
                        )}
                      >
                        <span className="select-none mr-4 text-muted-foreground w-8 inline-block text-right">
                          {line.lineNumber}
                        </span>
                        <span className="select-none mr-2">
                          {line.type === 'removed' ? '-' : line.type === 'added' ? '+' : ' '}
                        </span>
                        {line.line}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <pre className="p-4 rounded-md border font-mono text-xs overflow-x-auto max-h-96 bg-muted">
                  <code className="text-foreground">{fix.fixed_code}</code>
                </pre>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

// ============================================================================
// SERVICE DEPENDENCY GRAPH COMPONENT
// ============================================================================

interface DependencyNodeData extends Record<string, unknown> {
  label: string;
  kind: string;
  namespace: string;
  isPrimary: boolean;
  isExternal: boolean;
  incidentCount: number;
  incidentSeverity: string;
  incidentId: string;
  isExpanded: boolean;
  isExpandable: boolean;
  isLoading: boolean;
  isBidirectional: boolean;
  direction: 'source' | 'destination' | 'primary';
}

const DependencyNode: React.FC<{ data: DependencyNodeData }> = ({ data }) => {
  const hasIncident = !data.isPrimary && data.incidentCount > 0;

  const getBorderStyles = () => {
    if (data.isPrimary) {
      return 'border-2 border-red-500 bg-red-50 dark:border-red-400 dark:bg-red-950/30 ring-2 ring-red-200 dark:ring-red-900/50';
    }
    if (data.isExpanded) {
      return 'border-2 border-red-500 bg-red-50/50 dark:border-red-400 dark:bg-red-950/20 ring-1 ring-red-200 dark:ring-red-900/30';
    }
    if (hasIncident) {
      return 'border-2 border-dashed animate-incident-border-pulse';
    }
    if (data.isExternal) {
      return 'border-2 border-orange-400 bg-orange-50 dark:border-orange-500 dark:bg-orange-950/20';
    }
    return 'border-2 border-purple-400 bg-purple-50 dark:border-purple-500 dark:bg-purple-950/20';
  };

  const getIcon = () => {
    if (data.isLoading) return <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />;
    if (data.isExternal) return <Globe className="h-5 w-5 text-orange-500" />;
    return getWorkloadIcon(data.kind, 'h-5 w-5');
  };

  const displayName = data.label.length > 22 ? data.label.substring(0, 22) + '...' : data.label;

  return (
    <div className="relative">
      <Handle type="target" position={Position.Left} className="!bg-muted-foreground !w-2 !h-2" />
      <Handle type="source" position={Position.Right} className="!bg-muted-foreground !w-2 !h-2" />
      <div className={cn(
        'rounded-lg p-3 shadow-sm transition-all duration-200 hover:shadow-md',
        'min-w-[140px] max-w-[180px]',
        (hasIncident || data.isExpandable) ? 'cursor-pointer' : 'cursor-default',
        getBorderStyles()
      )}>
        <div className="flex flex-col items-center gap-1.5 text-center">
          {getIcon()}
          <div className="leading-tight w-full">
            <h5 className="font-semibold text-xs text-foreground line-clamp-2" title={data.label}>
              {displayName}
            </h5>
            {!data.isExternal && data.namespace && (
              <p className="text-[10px] text-muted-foreground truncate">{data.namespace}</p>
            )}
            <p className="text-[10px] text-muted-foreground">{data.kind || (data.isExternal ? 'External' : 'Workload')}</p>
          </div>
          {data.isBidirectional && (
            <div className="text-[9px] text-emerald-500 font-medium">↔ bidirectional</div>
          )}
          {data.isPrimary && (
            <Badge variant="destructive" className="text-[9px] px-1.5 py-0">
              Affected
            </Badge>
          )}
          {hasIncident && (
            <Link
              to={`/incidents/${data.incidentId}`}
              onClick={(e) => e.stopPropagation()}
              className="no-underline"
            >
              <Badge variant="destructive" className="text-[9px] px-1.5 py-0 hover:bg-red-700 transition-colors">
                {data.incidentCount} incident{data.incidentCount !== 1 ? 's' : ''} →
              </Badge>
            </Link>
          )}
          {data.isExpandable && !data.isExpanded && !data.isLoading && (
            <div className="flex items-center gap-1 text-[9px] text-muted-foreground">
              <ChevronRight className="h-3 w-3" />
              <span>Expand deps</span>
            </div>
          )}
          {data.isExpanded && (
            <div className="flex items-center gap-1 text-[9px] text-indigo-500">
              <ChevronDown className="h-3 w-3" />
              <span>Collapse</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const dependencyNodeTypes = { dependency: DependencyNode };

interface ServiceDependencyGraphProps {
  incident: IncidentDetails;
  affectedResources: any[];
}

// Unified flow entry used for graph building — works for both L3/L4 and L7 data
interface UnifiedFlow {
  src_namespace: string;
  src_kind: string;
  src_name: string;
  dst_namespace: string;
  dst_kind: string;
  dst_name: string;
  count: number;
  allowed: boolean;
  src_known_ip_name?: string;
  dst_known_ip_name?: string;
}

// Clean L7 access log endpoint names using the same logic as the topology map's
// convertToDisplayFormat (utils.ts lines 245-256): strip the redundant namespace
// suffix that Istio/Envoy appends to pod names in L7 logs.
// e.g. "api-gateway-59567b85d6-d97d2.demo-rca" → "api-gateway-59567b85d6-d97d2"
function cleanL7EndpointName(name: string, kind: string, namespace: string): string {
  if (kind === 'Pod' && name.endsWith(`.${namespace}`)) {
    return name.slice(0, -(`.${namespace}`.length));
  }
  return name;
}

// Shared helpers for flow processing
function mergeResponseToUnifiedFlows(response: { flows?: any[]; l7_access_logs?: any[] }): UnifiedFlow[] {
  const unified: UnifiedFlow[] = [];
  for (const f of (response.flows || [])) {
    if (!f.src_name && !f.dst_name) continue;
    unified.push({ src_namespace: f.src_namespace, src_kind: f.src_kind, src_name: f.src_name, dst_namespace: f.dst_namespace, dst_kind: f.dst_kind, dst_name: f.dst_name, count: f.count, allowed: f.allowed, src_known_ip_name: f.src_known_ip_name, dst_known_ip_name: f.dst_known_ip_name });
  }
  for (const l of (response.l7_access_logs || [])) {
    if (!l.src_name || !l.dst_name) continue;
    unified.push({ src_namespace: l.src_namespace, src_kind: l.src_kind, src_name: cleanL7EndpointName(l.src_name, l.src_kind, l.src_namespace), dst_namespace: l.dst_namespace, dst_kind: l.dst_kind, dst_name: cleanL7EndpointName(l.dst_name, l.dst_kind, l.dst_namespace), count: l.count, allowed: l.allowed });
  }
  return unified;
}

function matchesWorkloadName(flowName: string, flowNs: string, workloadName: string, workloadNs: string): boolean {
  if (flowNs !== workloadNs) return false;
  return flowName === workloadName || flowName.startsWith(workloadName + '-');
}

function resolveToWorkloadName(name: string, kind: string): { name: string; kind: string } {
  if (kind !== 'Pod') return { name, kind };
  const parts = name.split('-');
  if (parts.length >= 3) {
    const candidate = parts.slice(0, -2).join('-');
    if (candidate.length > 0) return { name: candidate, kind: 'Workload' };
  }
  return { name, kind: 'Pod' };
}

function workloadKey(ns: string, name: string): string {
  return `${ns}/${name}`;
}

interface NeighborInfo {
  name: string;
  namespace: string;
  kind: string;
  count: number;
}

function extractNeighbors(flows: UnifiedFlow[], workloadName: string, workloadNs: string): { sources: Map<string, NeighborInfo>; destinations: Map<string, NeighborInfo> } {
  const sources = new Map<string, NeighborInfo>();
  const destinations = new Map<string, NeighborInfo>();

  flows.forEach(flow => {
    const isSrcMatch = matchesWorkloadName(flow.src_name, flow.src_namespace, workloadName, workloadNs);
    const isDstMatch = matchesWorkloadName(flow.dst_name, flow.dst_namespace, workloadName, workloadNs);

    if (isSrcMatch && !isDstMatch) {
      const ns = flow.dst_namespace;
      const isExt = !ns || ns === '' || ns === 'external' || flow.dst_kind === 'External' || flow.dst_kind === 'Unmanaged';
      if (!isExt) {
        const resolved = resolveToWorkloadName(flow.dst_name, flow.dst_kind);
        const key = workloadKey(ns, resolved.name);
        const existing = destinations.get(key);
        const prevNs = existing?.namespace || '';
        const useNew = !existing || (prevNs === 'default' && ns !== 'default');
        const preferKind = !existing || (existing.kind === 'Pod' && resolved.kind !== 'Pod') || (existing.kind === 'Workload' && !['Pod', 'Workload'].includes(resolved.kind));
        destinations.set(key, { name: resolved.name, namespace: useNew ? ns : prevNs, kind: preferKind ? resolved.kind : (existing?.kind || resolved.kind), count: (existing?.count || 0) + (flow.count || 1) });
      }
    }
    if (isDstMatch && !isSrcMatch) {
      const ns = flow.src_namespace;
      const isExt = !ns || ns === '' || ns === 'external' || flow.src_kind === 'External' || flow.src_kind === 'Unmanaged';
      if (!isExt) {
        const resolved = resolveToWorkloadName(flow.src_name, flow.src_kind);
        const key = workloadKey(ns, resolved.name);
        const existing = sources.get(key);
        const prevNs = existing?.namespace || '';
        const useNew = !existing || (prevNs === 'default' && ns !== 'default');
        const preferKind = !existing || (existing.kind === 'Pod' && resolved.kind !== 'Pod') || (existing.kind === 'Workload' && !['Pod', 'Workload'].includes(resolved.kind));
        sources.set(key, { name: resolved.name, namespace: useNew ? ns : prevNs, kind: preferKind ? resolved.kind : (existing?.kind || resolved.kind), count: (existing?.count || 0) + (flow.count || 1) });
      }
    }
  });
  return { sources, destinations };
}

interface ExpandedNodeData {
  flows: UnifiedFlow[];
  sources: Map<string, NeighborInfo>;
  destinations: Map<string, NeighborInfo>;
  degree: number;
}

function ServiceDependencyGraphInner({ incident, affectedResources }: ServiceDependencyGraphProps) {
  const [flowData, setFlowData] = useState<UnifiedFlow[]>([]);
  const [neighborIncidents, setNeighborIncidents] = useState<Map<string, { count: number; severity: string; namespace: string; incidentId: string }>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Map<string, ExpandedNodeData>>(new Map());
  const [loadingNodes, setLoadingNodes] = useState<Set<string>>(new Set());
  const { fitView } = useReactFlow();
  const navigate = useNavigate();

  const signals = incident.signals || [];

  const primaryWorkload = useMemo(() => {
    const workloadTypes = ['Deployment', 'StatefulSet', 'DaemonSet', 'Job', 'CronJob'];
    const best = affectedResources.find((r: any) =>
      workloadTypes.includes(r.type || r.kind || r.resource_type)
    ) || affectedResources.find((r: any) =>
      (r.type || r.kind || r.resource_type) !== 'Node' && (r.type || r.kind || r.resource_type) !== 'Pod'
    );
    if (best?.name && best?.namespace) {
      return { name: best.name, namespace: best.namespace, kind: best.type || best.kind || best.resource_type || 'Workload' };
    }
    if (signals.length > 0 && signals[0].resource_name && signals[0].namespace) {
      const resourceName = signals[0].resource_name;
      const resourceKind = signals[0].resource_kind || '';
      // Only strip pod hash suffixes if the resource is a Pod
      if (resourceKind.toLowerCase() === 'pod') {
        const resolved = resolveToWorkloadName(resourceName, 'Pod');
        return { name: resolved.name, namespace: signals[0].namespace, kind: resolved.kind };
      }
      return { name: resourceName, namespace: signals[0].namespace, kind: resourceKind || 'Workload' };
    }
    return null;
  }, [affectedResources, signals]);

  // Fetch primary workload flows + neighbor incident data
  useEffect(() => {
    if (!primaryWorkload) { setIsLoading(false); return; }
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiClient.getWorkloadFlows({ cluster_id: incident.cluster_id, namespace: primaryWorkload.namespace, workload_name: primaryWorkload.name, limit: 500 });
        if (cancelled) return;
        const unified = mergeResponseToUnifiedFlows(response);
        setFlowData(unified);

        // Collect neighbor names for incident lookup.
        // Resolve pod names to workload names (same as graph builder) so
        // the incidentMap keys match the graph node keys.
        const neighborMap = new Map<string, string>();
        unified.forEach(flow => {
          const addIfNeighbor = (name: string, kind: string, ns: string) => {
            if (matchesWorkloadName(name, ns, primaryWorkload.name, primaryWorkload.namespace)) return;
            if (!ns || ns === '' || ns === 'external' || kind === 'External' || kind === 'Unmanaged') return;
            const resolved = resolveToWorkloadName(name, kind);
            const key = workloadKey(ns, resolved.name);
            const prev = neighborMap.get(key);
            if (!prev || (prev === 'default' && ns !== 'default')) neighborMap.set(key, ns);
          };
          addIfNeighbor(flow.src_name, flow.src_kind, flow.src_namespace);
          addIfNeighbor(flow.dst_name, flow.dst_kind, flow.dst_namespace);
        });

        const incidentMap = new Map<string, { count: number; severity: string; namespace: string; incidentId: string }>();
        const entries = Array.from(neighborMap.entries());
        const BATCH_SIZE = 5;
        for (let i = 0; i < entries.length; i += BATCH_SIZE) {
          const batch = entries.slice(i, i + BATCH_SIZE);
          await Promise.all(batch.map(async ([qualifiedKey, ns]) => {
            try {
              const bareName = qualifiedKey.split('/').slice(1).join('/');
              const res = await apiClient.getIncidentsByResource({ cluster_id: incident.cluster_id, resource_type: 'workload', namespace: ns, workload: bareName });
              if (res.count > 0 && res.incidents?.length > 0) {
                const highestSeverity = res.incidents.reduce((h: string, inc: TopologyIncident) => { const o = ['critical','high','medium','low']; return o.indexOf(inc.severity) < o.indexOf(h) ? inc.severity : h; }, 'low');
                incidentMap.set(qualifiedKey, { count: res.incidents.length, severity: highestSeverity, namespace: ns, incidentId: res.incidents[0].id });
              }
            } catch { /* ignore */ }
          }));
        }
        if (!cancelled) setNeighborIncidents(incidentMap);
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Failed to load service dependencies');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incident.cluster_id, primaryWorkload?.name, primaryWorkload?.namespace]);

  // Toggle expansion — only one node per degree level can be expanded at a time
  const toggleExpand = useCallback(async (workloadName: string, namespace: string, degree: number) => {
    const nodeKey = workloadKey(namespace, workloadName);
    if (expandedNodes.has(nodeKey)) {
      setExpandedNodes(prev => { const next = new Map(prev); next.delete(nodeKey); return next; });
      return;
    }
    // Close any other expanded node at the same degree level
    setExpandedNodes(prev => {
      const next = new Map(prev);
      const toDelete: string[] = [];
      next.forEach((val, key) => { if (val.degree === degree) toDelete.push(key); });
      toDelete.forEach(key => { next.delete(key); });
      return next;
    });
    setLoadingNodes(prev => new Set(prev).add(nodeKey));
    try {
      const response = await apiClient.getWorkloadFlows({ cluster_id: incident.cluster_id, namespace, workload_name: workloadName, limit: 500 });
      const flows = mergeResponseToUnifiedFlows(response);
      const { sources, destinations } = extractNeighbors(flows, workloadName, namespace);
      setExpandedNodes(prev => { const next = new Map(prev); next.set(nodeKey, { flows, sources, destinations, degree }); return next; });
    } catch { /* ignore errors for expansion */ }
    setLoadingNodes(prev => { const next = new Set(prev); next.delete(nodeKey); return next; });
  }, [incident.cluster_id, expandedNodes]);

  // Build graph with primary + expanded nodes
  const { nodes: graphNodes, edges: graphEdges } = useMemo(() => {
    if (!primaryWorkload || flowData.length === 0) return { nodes: [] as Node[], edges: [] as Edge[] };

    const { sources, destinations } = extractNeighbors(flowData, primaryWorkload.name, primaryWorkload.namespace);

    // Track bidirectional services (appear as both source and destination)
    const bidirectionalKeys = new Set<string>();
    sources.forEach((_, key) => { if (destinations.has(key)) bidirectionalKeys.add(key); });

    const topSources = Array.from(sources.entries()).sort((a, b) => b[1].count - a[1].count).slice(0, 8);
    const topDests = Array.from(destinations.entries()).sort((a, b) => b[1].count - a[1].count).slice(0, 8);

    const nodes: Node[] = [];
    const edges: Edge[] = [];
    const edgeColor = '#10b981';
    const ySpacing = 160;
    const colWidth = 300;

    // Collect expanded destination names for multi-column layout
    const expandedDstNames = new Set<string>();
    topDests.forEach(([key]) => { if (expandedNodes.has(key)) expandedDstNames.add(key); });
    const expandedSrcNames = new Set<string>();
    topSources.forEach(([key]) => { if (expandedNodes.has(key)) expandedSrcNames.add(key); });

    // Column X positions: sources | primary | destinations | expanded-dest-deps
    const srcX = 50;
    const primaryX = srcX + colWidth;
    const dstX = primaryX + colWidth;
    const expandedDstX = dstX + colWidth;
    const expandedSrcX = srcX - colWidth;

    // Compute max height across all columns for centering
    const srcCount = topSources.length;
    const dstCount = topDests.length;

    // For expanded nodes, count their second-degree deps
    let maxExpandedDstDeps = 0;
    let maxExpandedSrcDeps = 0;
    expandedDstNames.forEach(name => {
      const data = expandedNodes.get(name);
      if (data) maxExpandedDstDeps = Math.max(maxExpandedDstDeps, data.destinations.size);
    });
    expandedSrcNames.forEach(name => {
      const data = expandedNodes.get(name);
      if (data) maxExpandedSrcDeps = Math.max(maxExpandedSrcDeps, data.sources.size);
    });

    const maxSide = Math.max(srcCount, dstCount, maxExpandedDstDeps, maxExpandedSrcDeps, 1);
    const totalHeight = (maxSide - 1) * ySpacing;
    const centerY = totalHeight / 2;

    const makeNodeData = (label: string, kind: string, ns: string, isPrimary: boolean, dir: string, incData?: { count: number; severity: string; incidentId: string }, isExpanded = false, isExpandable = false, isNodeLoading = false, isBidir = false): DependencyNodeData => ({
      label, kind, namespace: ns, isPrimary, isExternal: false,
      incidentCount: incData?.count || 0, incidentSeverity: incData?.severity || '', incidentId: incData?.incidentId || '',
      isExpanded, isExpandable, isLoading: isNodeLoading, isBidirectional: isBidir,
      direction: dir as any,
    });

    // Primary node
    nodes.push({ id: 'primary', type: 'dependency', position: { x: primaryX, y: centerY }, data: makeNodeData(primaryWorkload.name, primaryWorkload.kind, primaryWorkload.namespace, true, 'primary') });

    // Source nodes (left of primary)
    const srcOff = centerY - ((srcCount - 1) * ySpacing) / 2;
    topSources.forEach(([key, info], idx) => {
      const incData = neighborIncidents.get(key);
      const isExp = expandedNodes.has(key);
      const isExpandable = !!incData && incData.count > 0;
      const isNodeLoading = loadingNodes.has(key);
      const isBidir = bidirectionalKeys.has(key);
      nodes.push({ id: `src-${key}`, type: 'dependency', position: { x: srcX, y: srcOff + idx * ySpacing }, data: makeNodeData(info.name, info.kind, info.namespace, false, 'source', incData, isExp, isExpandable, isNodeLoading, isBidir) });
      edges.push({ id: `edge-src-${key}`, source: `src-${key}`, target: 'primary', type: 'default', animated: true, style: { stroke: edgeColor, strokeWidth: 2 }, markerEnd: { type: MarkerType.ArrowClosed, color: edgeColor } });

      // If expanded, show this node's sources further left, centered on the parent node
      if (isExp) {
        const parentY = srcOff + idx * ySpacing;
        const expData = expandedNodes.get(key)!;
        const primaryKey = workloadKey(primaryWorkload.namespace, primaryWorkload.name);
        const expSources = Array.from(expData.sources.entries()).filter(([n]) => n !== primaryKey).sort((a, b) => b[1].count - a[1].count).slice(0, 6);
        const expOff = parentY - ((expSources.length - 1) * ySpacing) / 2;
        expSources.forEach(([depKey, depInfo], depIdx) => {
          const depId = `exp-src-${key}-${depKey}`;
          const depIncData = neighborIncidents.get(depKey);
          const depIsExp = expandedNodes.has(depKey);
          const depIsExpandable = !!depIncData && depIncData.count > 0;
          const depIsLoading = loadingNodes.has(depKey);
          nodes.push({ id: depId, type: 'dependency', position: { x: expandedSrcX, y: expOff + depIdx * ySpacing }, data: makeNodeData(depInfo.name, depInfo.kind, depInfo.namespace, false, 'source', depIncData, depIsExp, depIsExpandable, depIsLoading) });
          edges.push({ id: `edge-${depId}`, source: depId, target: `src-${key}`, type: 'default', animated: true, style: { stroke: edgeColor, strokeWidth: 2 }, markerEnd: { type: MarkerType.ArrowClosed, color: edgeColor } });
        });
      }
    });

    // Destination nodes (right of primary)
    const dstOff = centerY - ((dstCount - 1) * ySpacing) / 2;
    topDests.forEach(([key, info], idx) => {
      const incData = neighborIncidents.get(key);
      const isExp = expandedNodes.has(key);
      const isExpandable = !!incData && incData.count > 0;
      const isNodeLoading = loadingNodes.has(key);
      const isBidir = bidirectionalKeys.has(key);
      nodes.push({ id: `dst-${key}`, type: 'dependency', position: { x: dstX, y: dstOff + idx * ySpacing }, data: makeNodeData(info.name, info.kind, info.namespace, false, 'destination', incData, isExp, isExpandable, isNodeLoading, isBidir) });
      edges.push({ id: `edge-dst-${key}`, source: 'primary', target: `dst-${key}`, type: 'default', animated: true, style: { stroke: edgeColor, strokeWidth: 2 }, markerEnd: { type: MarkerType.ArrowClosed, color: edgeColor } });

      // If expanded, show this node's destinations further right, centered on the parent node
      if (isExp) {
        const parentY = dstOff + idx * ySpacing;
        const expData = expandedNodes.get(key)!;
        const primaryKey = workloadKey(primaryWorkload.namespace, primaryWorkload.name);
        const expDests = Array.from(expData.destinations.entries()).filter(([n]) => n !== primaryKey).sort((a, b) => b[1].count - a[1].count).slice(0, 6);
        const expOff = parentY - ((expDests.length - 1) * ySpacing) / 2;
        expDests.forEach(([depKey, depInfo], depIdx) => {
          const depId = `exp-dst-${key}-${depKey}`;
          const depIncData = neighborIncidents.get(depKey);
          const depIsExp = expandedNodes.has(depKey);
          const depIsExpandable = !!depIncData && depIncData.count > 0;
          const depIsLoading = loadingNodes.has(depKey);
          nodes.push({ id: depId, type: 'dependency', position: { x: expandedDstX, y: expOff + depIdx * ySpacing }, data: makeNodeData(depInfo.name, depInfo.kind, depInfo.namespace, false, 'destination', depIncData, depIsExp, depIsExpandable, depIsLoading) });
          edges.push({ id: `edge-${depId}`, source: `dst-${key}`, target: depId, type: 'default', animated: true, style: { stroke: edgeColor, strokeWidth: 2 }, markerEnd: { type: MarkerType.ArrowClosed, color: edgeColor } });
        });
      }
    });

    return { nodes, edges };
  }, [primaryWorkload, flowData, neighborIncidents, expandedNodes, loadingNodes]);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  useEffect(() => {
    setNodes(graphNodes);
    setEdges(graphEdges);
    if (graphNodes.length > 0) {
      const timer = setTimeout(() => fitView({ padding: 0.2, duration: 300 }), 50);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphNodes, graphEdges]);

  // Click: expand/collapse incident nodes, navigate to incident via badge
  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    const data = node.data as DependencyNodeData;
    if (data.isPrimary) return;
    if (data.isExpandable || data.isExpanded) {
      // Degree 1 for src-*/dst-* nodes, degree 2 for exp-* nodes
      const degree = node.id.startsWith('exp-') ? 2 : 1;
      toggleExpand(data.label, data.namespace, degree);
    } else if (data.incidentId && data.incidentCount > 0) {
      navigate(`/incidents/${data.incidentId}`);
    }
  }, [navigate, toggleExpand]);

  if (!primaryWorkload || isLoading || error || graphNodes.length <= 1) return null;

  const incidentNeighbors = Array.from(neighborIncidents.entries());

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Service Dependencies
        </CardTitle>
        <CardDescription>
          Services communicating with {primaryWorkload.namespace}/{primaryWorkload.name}
          {incidentNeighbors.length > 0 && (
            <span className="text-red-500 font-medium ml-1">
              — {incidentNeighbors.length} dependent service{incidentNeighbors.length !== 1 ? 's' : ''} also experiencing incidents
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <div className="h-[400px] w-full border-t">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            nodeTypes={dependencyNodeTypes}
            fitView
            fitViewOptions={{ padding: 0.3 }}
            minZoom={0.3}
            maxZoom={1.5}
            proOptions={{ hideAttribution: true }}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={true}
          >
            <Background />
            <Controls showInteractive={false} />
          </ReactFlow>
        </div>
        {incidentNeighbors.length > 0 && (
          <div className="px-4 py-3 border-t bg-red-50/50 dark:bg-red-950/10">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <span className="text-sm font-medium text-red-700 dark:text-red-300">
                Related Incidents on Dependent Services
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {incidentNeighbors.map(([key, data]) => (
                <Badge
                  key={key}
                  variant="outline"
                  className={cn(
                    'text-xs',
                    data.severity === 'critical' && 'border-red-300 text-red-700 dark:text-red-300',
                    data.severity === 'high' && 'border-orange-300 text-orange-700 dark:text-orange-300',
                    data.severity === 'medium' && 'border-yellow-300 text-yellow-700 dark:text-yellow-300',
                    data.severity === 'low' && 'border-blue-300 text-blue-700 dark:text-blue-300',
                  )}
                >
                  {data.namespace}/{key} ({data.count} incident{data.count !== 1 ? 's' : ''})
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ServiceDependencyGraph(props: ServiceDependencyGraphProps) {
  return (
    <ReactFlowProvider>
      <ServiceDependencyGraphInner {...props} />
    </ReactFlowProvider>
  );
}

// ============================================================================
// MAIN INCIDENT DETAIL COMPONENT
// ============================================================================

export default function IncidentDetail() {
  const { incidentId } = useParams<{ incidentId: string }>();
  const [incident, setIncident] = useState<IncidentDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cluster, setCluster] = useState<Cluster | undefined>(undefined);
  
  // Manual resolution and validation state
  const [markingResolved, setMarkingResolved] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [showAllChainOfEvents, setShowAllChainOfEvents] = useState(false);
  const [showAllTimeline, setShowAllTimeline] = useState(false);
  const [showChat, setShowChat] = useState(() => window.matchMedia('(min-width: 768px)').matches);
  const [chatMounted, setChatMounted] = useState(() => window.matchMedia('(min-width: 768px)').matches);

  // Post-mortem analysis state (auto-loaded from incident data)
  const [postMortemData, setPostMortemData] = useState<any>(null);
  
  // Code fix PR state
  const [codeFixPrUrl, setCodeFixPrUrl] = useState<string | null>(null);
  const [creatingCodeFixPR, setCreatingCodeFixPR] = useState(false);
  const [codeFixPrError, setCodeFixPrError] = useState<string | null>(null);
  const [isRetryingCodeAnalysis, setIsRetryingCodeAnalysis] = useState(false);
  const [codeAnalysisRetryError, setCodeAnalysisRetryError] = useState<string | null>(null);

  // Incident history state (previous occurrences of the same issue)
  const [previousIncidents, setPreviousIncidents] = useState<any[]>([]);
  const [historyPage, setHistoryPage] = useState(0);
  const HISTORY_PER_PAGE = 5;

  // Suspected source files pagination
  const [sourceFilesPage, setSourceFilesPage] = useState(0);
  const SOURCE_FILES_PER_PAGE = 3;

  // Impact assessment editing state
  const [editingImpact, setEditingImpact] = useState(false);
  const [savingImpact, setSavingImpact] = useState(false);
  const [impactFormData, setImpactFormData] = useState({
    user_impact: '',
    duration_minutes: 0,
    affected_users: '',
    business_impact: '',
    technical_impact: ''
  });

  useEffect(() => {
    async function fetchIncidentDetails() {
      if (!incidentId) {
        setError('No incident ID provided');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await apiClient.getIncidentDetails(incidentId);
        setIncident(response);

        // Initialize code fix PR state from backend (persists across page refresh)
        setCodeFixPrUrl(response.code_fix_pr_url || null);

        // Load saved validation result if present
        if (response.validation_status && response.validated_at) {
          setValidationResult({
            success: true,
            validation_status: response.validation_status,
            validation_summary: response.validation_summary || '',
            validation_details: response.validation_details || [],
            checked_at: new Date(response.validated_at * 1000).toISOString()
          });
          
          // If validation is still in progress, start polling
          if (response.validation_status === 'in_progress') {
            pollForValidationCompletion();
          }
        }
        
        // Load post-mortem analysis if available
        if (response.postmortem_generated && response.postmortem_result) {
          setPostMortemData(response.postmortem_result);
          // Initialize impact form data if available
          if (response.postmortem_result.impact_assessment) {
            setImpactFormData({
              user_impact: response.postmortem_result.impact_assessment.user_impact || '',
              duration_minutes: response.postmortem_result.impact_assessment.duration_minutes || 0,
              affected_users: response.postmortem_result.impact_assessment.affected_users || '',
              business_impact: response.postmortem_result.impact_assessment.business_impact || '',
              technical_impact: response.postmortem_result.impact_assessment.technical_impact || ''
            });
          }
        }
        
        // Load previous incidents (history) — summaries are returned inline by the server
        if (response.previous_incidents && response.previous_incidents.length > 0) {
          setPreviousIncidents(response.previous_incidents);
        } else {
          setPreviousIncidents([]);
        }
        setHistoryPage(0);

        // Fetch cluster information to check safe_apply status
        if (response.cluster_id) {
          try {
            const clustersResponse = await apiClient.listClusters();
            const clusterInfo = clustersResponse.clusters?.find(c => c.id === response.cluster_id);
            setCluster(clusterInfo);
          } catch (clusterErr) {
            console.error('Failed to fetch cluster information:', clusterErr);
            // Don't fail the whole page if cluster fetch fails
          }
        }
      } catch (err: any) {
        console.error('Failed to fetch incident details:', err);
        setError(err.response?.data?.message || 'Failed to load incident details');
      } finally {
        setLoading(false);
      }
    }

    fetchIncidentDetails();
    setShowAllChainOfEvents(false);
    setShowAllTimeline(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidentId]);

  // Poll for updates while code analysis is in progress
  useEffect(() => {
    if (incident?.status !== 'analyzing_code' || !incidentId) return;

    const pollInterval = setInterval(async () => {
      try {
        const response = await apiClient.getIncidentDetails(incidentId);
        if (response.status !== 'analyzing_code') {
          clearInterval(pollInterval);
          setIncident(response);
        }
      } catch (err) {
        console.error('Code analysis polling failed:', err);
      }
    }, 5000);

    return () => clearInterval(pollInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incident?.status, incidentId]);

  const confirmMarkResolved = async () => {
    setMarkingResolved(true);
    setShowResolveDialog(false);
    
    try {
      const result = await apiClient.markIncidentAsResolved(incident!.id);
      console.log('Mark as resolved result:', result);
      
      if (result.success) {
        // Force refresh to show new status
        setTimeout(() => {
          window.location.reload();
        }, 500);
      } else {
        alert('Failed to mark incident as resolved');
        setMarkingResolved(false);
      }
    } catch (err: any) {
      console.error('Failed to mark resolved:', err);
      alert('Failed to mark incident as resolved: ' + (err.response?.data?.message || err.response?.data?.error || err.message));
      setMarkingResolved(false);
    }
  };

  const handleValidateFix = async () => {
    setValidating(true);
    setValidationResult(null);
    
    try {
      const result = await apiClient.validateIncidentFix(incident!.id);
      setValidationResult(result);
      
      // If validation started async (in_progress), poll for completion
      if (result.validation_status === 'in_progress') {
        pollForValidationCompletion();
      }
    } catch (err: any) {
      console.error('Failed to validate fix:', err);
      setValidationResult({
        success: false,
        validation_status: 'error',
        validation_summary: 'Failed to validate fix: ' + (err.response?.data?.error || err.message),
        validation_details: [],
        checked_at: new Date().toISOString()
      });
    } finally {
      setValidating(false);
    }
  };

  const handleSaveImpactAssessment = async () => {
    setSavingImpact(true);
    
    try {
      await apiClient.updateImpactAssessment(incident!.id, impactFormData);
      
      // Update local state
      setPostMortemData({
        ...postMortemData,
        impact_assessment: impactFormData
      });
      
      setEditingImpact(false);
    } catch (err: any) {
      console.error('Failed to save impact assessment:', err);
      alert('Failed to save impact assessment: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingImpact(false);
    }
  };

  const handleCancelImpactEdit = () => {
    // Reset form data to current values
    if (postMortemData?.impact_assessment) {
      setImpactFormData({
        user_impact: postMortemData.impact_assessment.user_impact || '',
        duration_minutes: postMortemData.impact_assessment.duration_minutes || 0,
        affected_users: postMortemData.impact_assessment.affected_users || '',
        business_impact: postMortemData.impact_assessment.business_impact || '',
        technical_impact: postMortemData.impact_assessment.technical_impact || ''
      });
    }
    setEditingImpact(false);
  };

  
  // Poll for validation completion
  const pollForValidationCompletion = () => {
    if (!incidentId) {
      console.error('Cannot poll without incident ID');
      return;
    }
    
    console.log('Starting validation polling for incident', incidentId);
    const pollInterval = setInterval(async () => {
      try {
        const response = await apiClient.getIncidentDetails(incidentId);
        
        // Check if validation completed
        if (response.validation_status && response.validation_status !== 'in_progress') {
          clearInterval(pollInterval);
          console.log('Validation completed:', response.validation_status);
          
          // Update validation result
          setValidationResult({
            success: true,
            validation_status: response.validation_status,
            validation_summary: response.validation_summary || '',
            validation_details: response.validation_details || [],
            checked_at: new Date(response.validated_at! * 1000).toISOString()
          });
          
          // Refresh incident data
          const currentStatus = incident?.status;
          setIncident(response);
          
          // If status changed (e.g., resolved → remediation_applied after validation confirms fix),
          // reload page to update UI properly
          if (response.status !== currentStatus) {
            console.log('Incident status changed from', currentStatus, 'to', response.status, '- reloading page');
            setTimeout(() => {
              window.location.reload();
            }, 1500); // Give user time to see the validation result first
          }
        } else {
          console.log('Validation still in progress, continuing to poll...');
        }
      } catch (err) {
        console.error('Failed to poll validation status:', err);
        clearInterval(pollInterval);
      }
    }, 5000); // Poll every 5 seconds
    
    // Stop polling after 2 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      console.log('Stopped polling after 2 minutes');
    }, 120000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !incident) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error || 'Incident not found'}</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Ensure signals array is defined
  const signals = incident.signals || [];
  const hasRCA = !!incident.rca_result;
  const timeline = incident.rca_result?.timeline || [];
  const yamlFixes = incident.rca_result?.yaml_fixes || [];
  const codeFixes = incident.rca_result?.code_fixes || [];
  const causalPRs = incident.rca_result?.causal_prs || [];
  const isApplicationLevelFailure = incident.rca_result?.is_application_level_failure || false;
  const suspectedSourceFiles = incident.rca_result?.suspected_source_files || [];
  const stackTraceSummary = incident.rca_result?.stack_trace_summary || '';

  // Parse affected_resources — prefer investigation_summary.affected_resources (from LLM investigation)
  // which has accurate K8s data, over top-level affected_resources (extracted from signals, may be inaccurate for PD incidents)
  let affectedResources: any[] = [];
  if (incident.rca_result?.investigation_summary?.affected_resources) {
    const summaryResources = incident.rca_result.investigation_summary.affected_resources;
    affectedResources = Array.isArray(summaryResources) ? summaryResources : [];
  } else if (incident.rca_result?.affected_resources) {
    try {
      const parsed = typeof incident.rca_result.affected_resources === 'string'
        ? JSON.parse(incident.rca_result.affected_resources)
        : incident.rca_result.affected_resources;
      affectedResources = Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.error('Failed to parse affected_resources:', e);
      affectedResources = [];
    }
  }

  // Compute primary resource display from investigation_summary affected resources (most accurate)
  // Falls back to signal data for non-RCA incidents
  const primaryResourceDisplay = (() => {
    // Prefer the best resource from RCA affected resources (Deployment > Pod > other)
    if (affectedResources.length > 0) {
      const best = affectedResources.find((r: any) =>
        ['Deployment', 'StatefulSet', 'DaemonSet', 'Job', 'CronJob'].includes(r.type || r.kind || r.resource_type)
      ) || affectedResources.find((r: any) =>
        (r.type || r.kind || r.resource_type) !== 'Node'
      ) || affectedResources[0];

      const ns = best.namespace;
      const name = best.name;
      if (ns && name) return `${ns}/${name}`;
      if (name) return name;
    }
    // Fallback to signal data
    if (signals.length > 0) {
      return signals[0].namespace
        ? `${signals[0].namespace}/${signals[0].resource_name}`
        : signals[0].resource_name;
    }
    return 'Unknown resource';
  })();

  return (
    <div className="flex flex-col -mx-4 sm:-mx-6 lg:-mx-8 -my-4 overflow-hidden" style={{ height: '100dvh' }}>
      {/* Page Header — fixed at top */}
      <div className="flex-shrink-0 px-4 sm:px-6 lg:px-8 py-4 border-b border-border bg-background">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 min-w-0">
            <MobileMenuButton />
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-bold tracking-tight min-w-0">
                  {incident.rca_result?.incident_type && incident.rca_result.incident_type !== 'Unknown' && incident.rca_result.incident_type !== 'Failed'
                    ? getIncidentTypeLabel(incident.rca_result.incident_type)
                    : getIncidentTypeLabel(incident.type)}
                </h1>
                <Badge variant={incident.status === 'resolved' ? 'outline' : 'default'}>
                  {getStatusLabel(incident.status)}
                </Badge>
                <Badge variant={incident.severity === 'critical' ? 'destructive' : 'secondary'}>
                  {incident.severity}
                </Badge>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2 min-w-0 flex-wrap">
                <span className="font-mono min-w-0 truncate">
                  {primaryResourceDisplay}
                </span>
                <span className="text-muted-foreground mx-2">in</span>
                <span>{incident.cluster_name}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              aria-label={showChat ? 'Hide Chat' : 'Show Chat'}
              onClick={() => {
                if (!showChat) setChatMounted(true);
                setShowChat(!showChat);
              }}
              className="flex items-center gap-2"
            >
              {showChat ? (
                <>
                  <X className="h-4 w-4" />
                  <span className="hidden sm:inline">Hide Chat</span>
                </>
              ) : (
                <>
                  <MessageSquare className="h-4 w-4" />
                  <span className="hidden sm:inline">Show Chat</span>
                </>
              )}
            </Button>
            <ModeToggle />
          </div>
        </div>
      </div>

      {/* Content area — main + chat side by side */}
      <div className="flex flex-col md:flex-row flex-1 overflow-hidden min-h-0">
      {/* Main Content Area */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 sm:px-6 lg:px-8 py-6 min-w-0">
        <div className={cn("space-y-6", showChat ? "md:pr-1" : "")}>

      {/* Status Alert */}
      {incident.status === 'investigating' && (
        <Alert>
          <Loader2 className="h-4 w-4 animate-spin" />
          <AlertTitle>AI Investigation in Progress</AlertTitle>
          <AlertDescription>
            Our AI agents are analyzing this incident to determine the root cause and generate fixes.
          </AlertDescription>
        </Alert>
      )}

      {/* Transient Incident Alert */}
      {incident.rca_result?.transient && (
        <Alert className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          <AlertTitle className="text-blue-800 dark:text-blue-200">Transient Incident</AlertTitle>
          <AlertDescription className="text-blue-700 dark:text-blue-300">
            This incident was classified as transient, which means it is a temporary issue caused by normal cluster operations
            (node lifecycle, autoscaling, CNI initialization, etc.) that resolved on its own. See the Root Cause Analysis below for details.
          </AlertDescription>
        </Alert>
      )}

      {/* Main Content */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Column - RCA and Investigation */}
        <div className="lg:col-span-2 space-y-6 min-w-0">
          {/* Root Cause (if available) */}
          {hasRCA && incident.rca_result && (
            <Card>
              <CardHeader>
                <CardTitle>
                  Root Cause Analysis
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-muted-foreground mb-2">Incident Type</h4>
                  <p className="text-base break-words">
                    {incident.rca_result.incident_type && incident.rca_result.incident_type !== 'Unknown'
                      ? incident.rca_result.incident_type
                      : getIncidentTypeLabel(incident.type)}
                  </p>
                </div>

                <Separator />

                <div>
                  <h4 className="text-sm font-semibold text-muted-foreground mb-2">Root Cause</h4>
                  <p className="text-base leading-relaxed break-words">{parseFormattedText(incident.rca_result.root_cause)}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Investigation Summary */}
          {hasRCA && incident.rca_result && incident.rca_result.investigation_summary &&
           typeof incident.rca_result.investigation_summary === 'object' &&
           incident.rca_result.investigation_summary.incident_summary && (
            <Card>
              <CardHeader>
                <CardTitle>
                  Investigation Summary
                </CardTitle>
                <CardDescription>
                  Detailed analysis of the incident and chain of events
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* 1. Incident Summary */}
                {incident.rca_result.investigation_summary.incident_summary && (
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-3">Incident Summary</h3>
                    <p className="text-base leading-relaxed text-foreground break-words">
                      {parseFormattedText(incident.rca_result.investigation_summary.incident_summary.text)}
                    </p>
                  </div>
                )}

                {/* 2. Timeline of Events */}
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">Chain of Events</h3>
                  <div className="space-y-2">
                    {incident.rca_result.investigation_summary.timeline_of_events
                      .slice(0, showAllChainOfEvents ? undefined : 10)
                      .map((event, idx) => (
                      <div key={idx}>
                        {/* Show "X minutes/hours before" indicator */}
                        {event.time_before && (
                          <div className="flex items-center justify-center my-2">
                            <div className="text-xs text-muted-foreground flex items-center gap-2">
                              <div className="h-4 w-0.5 bg-border" />
                              <span>↓ {event.time_before}</span>
                            </div>
                          </div>
                        )}

                        {/* Timeline event */}
                        <div className="flex items-start gap-3 p-3 rounded-md border bg-card hover:bg-muted/50 transition-colors">
                          <div className="flex-shrink-0 mt-0.5">
                            {getTimelineIcon(event.icon)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2 mb-1">
                              <span className="text-xs font-mono text-muted-foreground">{event.time}</span>
                              <span className="text-sm font-semibold text-foreground">{event.label}</span>
                            </div>
                            <p className="text-sm text-foreground leading-relaxed break-words">
                              {parseFormattedText(event.description)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                    {incident.rca_result.investigation_summary.timeline_of_events.length > 10 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-muted-foreground hover:text-foreground"
                        onClick={() => setShowAllChainOfEvents(!showAllChainOfEvents)}
                      >
                        <ChevronDown className={`h-4 w-4 mr-1 transition-transform ${showAllChainOfEvents ? 'rotate-180' : ''}`} />
                        {showAllChainOfEvents
                          ? 'Show less'
                          : `Show ${incident.rca_result.investigation_summary.timeline_of_events.length - 10} more events`}
                      </Button>
                    )}
                  </div>
                </div>

                {/* 3. Root Cause */}
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">Root Cause</h3>
                  <div className="border bg-card p-4 rounded-md space-y-3">
                    <p className="text-base font-semibold text-foreground">
                      {parseFormattedText(incident.rca_result.investigation_summary.root_cause.summary)}
                    </p>
                    {incident.rca_result.investigation_summary.root_cause.details && (
                      <ul className="space-y-2 list-disc list-inside">
                        {incident.rca_result.investigation_summary.root_cause.details.map((detail, idx) => (
                          <li key={idx} className="text-sm leading-relaxed text-foreground">
                            {parseFormattedText(detail)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                {/* 4. Affected Resources */}
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">Affected Resources</h3>
                  <div className="grid gap-2">
                    {incident.rca_result.investigation_summary.affected_resources.map((resource, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 rounded-md border bg-card">
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-xs">{resource.type}</Badge>
                            <span className="font-mono text-sm break-all">{resource.namespace}/{resource.name}</span>
                          </div>
                          {resource.status && (
                            <span className="text-xs text-muted-foreground mt-1 block">{resource.status}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 5. Resolution Recommendation */}
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">Resolution Recommendation</h3>
                  <div className="border bg-card p-4 rounded-md space-y-4">
                    <div className="flex items-start gap-2">
                      <p className="text-base font-semibold text-foreground">
                        {parseFormattedText(incident.rca_result.investigation_summary.resolution_recommendation.primary)}
                      </p>
                    </div>
                    
                    {incident.rca_result.investigation_summary.resolution_recommendation.steps && (
                      <div>
                        <h4 className="text-sm font-semibold text-foreground mb-2">Steps:</h4>
                        <ol className="space-y-2 list-decimal list-inside">
                          {incident.rca_result.investigation_summary.resolution_recommendation.steps.map((step, idx) => (
                            <li key={idx} className="text-sm leading-relaxed text-foreground">
                              {parseFormattedText(step)}
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                    
                    {incident.rca_result.investigation_summary.resolution_recommendation.prevention && (
                      <div className="pt-3 border-t">
                        <h4 className="text-sm font-semibold text-foreground mb-2">Prevention:</h4>
                        <p className="text-sm leading-relaxed text-foreground">
                          {parseFormattedText(incident.rca_result.investigation_summary.resolution_recommendation.prevention)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Evidence Metrics Carousel */}
          {hasRCA && incident.rca_result?.evidence_metrics && incident.rca_result.evidence_metrics.length > 0 && (
            <EvidenceMetricsCarousel metrics={incident.rca_result.evidence_metrics} />
          )}

          {/* Timeline */}
          {hasRCA && timeline.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>
                  Timeline
                </CardTitle>
                <CardDescription>
                  Chronological sequence of events leading to the incident
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {timeline
                    .slice()
                    .sort((a, b) => b.timestamp - a.timestamp) // Most recent first
                    .slice(0, showAllTimeline ? undefined : 10)
                    .map((event, index, arr) => (
                      <div key={index} className="flex gap-4">
                        <div className="flex flex-col items-center flex-shrink-0">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted border-2 border-background flex-shrink-0">
                            {getEventTypeIcon(event.event_type, event.severity)}
                          </div>
                          {index < arr.length - 1 && (
                            <div className="w-0.5 h-full bg-muted mt-2" />
                          )}
                        </div>

                        <div className="flex-1 pb-6">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-mono text-muted-foreground">
                              {formatTime(event.timestamp)}
                            </span>
                            <Badge variant="outline" className="text-xs capitalize">
                              {event.event_type === 'pagerduty_alert' ? 'PagerDuty Alert'
                                : event.event_type.replace('_', ' ')}
                            </Badge>
                            {event.source && event.source !== event.event_type && (
                              <span className="text-xs text-muted-foreground">• {event.source}</span>
                            )}
                          </div>
                          <p className="text-sm leading-relaxed">{event.description}</p>
                        </div>
                      </div>
                    ))}
                  {timeline.length > 10 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-muted-foreground hover:text-foreground"
                      onClick={() => setShowAllTimeline(!showAllTimeline)}
                    >
                      <ChevronDown className={`h-4 w-4 mr-1 transition-transform ${showAllTimeline ? 'rotate-180' : ''}`} />
                      {showAllTimeline
                        ? 'Show less'
                        : `Show ${timeline.length - 10} more events`}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Service Dependency Graph */}
          <ServiceDependencyGraph incident={incident} affectedResources={affectedResources} />

          {/* YAML Fixes/Resolutions */}
          {hasRCA && yamlFixes.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>
                  Remediation ({yamlFixes.length})
                </CardTitle>
                <CardDescription>
                  YAML manifest changes to resolve this incident
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {yamlFixes.map((fix, index) => (
                  <YAMLFixCard 
                    key={index} 
                    fix={fix} 
                    index={index}
                    incidentId={incident.id}
                    clusterId={incident.cluster_id}
                    cluster={cluster}
                  />
                ))}
              </CardContent>
            </Card>
          )}

          {/* Application-Level Failure Info */}
          {hasRCA && isApplicationLevelFailure && (suspectedSourceFiles.length > 0 || stackTraceSummary) && (
            <Card>
              <CardHeader>
                <CardTitle>
                  Application Code Issue Detected
                </CardTitle>
                <CardDescription>
                  This incident is caused by an application bug, not infrastructure configuration
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Stack Trace Summary */}
                {stackTraceSummary && (
                  <div>
                    <h4 className="text-sm font-medium mb-2">Stack Trace</h4>
                    <pre className="p-3 rounded-md bg-muted border font-mono text-xs overflow-x-auto max-h-32">
                      {stackTraceSummary}
                    </pre>
                  </div>
                )}

                {/* Suspected Source Files */}
                {suspectedSourceFiles.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-2">Suspected Source Files</h4>
                    <div className="space-y-2">
                      {suspectedSourceFiles
                        .slice(sourceFilesPage * SOURCE_FILES_PER_PAGE, (sourceFilesPage + 1) * SOURCE_FILES_PER_PAGE)
                        .map((file, index) => (
                        <div
                          key={index}
                          className="p-3 rounded-md bg-card border"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <FileCode className="h-4 w-4 text-muted-foreground" />
                            <span className="font-mono text-sm font-medium">{file.file_path}</span>
                            {file.line_number > 0 && (
                              <Badge variant="outline" className="text-xs">
                                Line {file.line_number}
                              </Badge>
                            )}
                          </div>
                          {file.function_name && (
                            <div className="text-xs text-muted-foreground mb-1">
                              Function: <code className="bg-muted px-1 rounded">{file.function_name}</code>
                            </div>
                          )}
                          {file.error_type && (
                            <Badge variant="outline" className="text-xs mb-1">
                              {file.error_type}
                            </Badge>
                          )}
                          {file.context && (
                            <p className="text-xs text-muted-foreground mt-1">{file.context}</p>
                          )}
                        </div>
                      ))}
                    </div>
                    {suspectedSourceFiles.length > SOURCE_FILES_PER_PAGE && (
                      <div className="flex items-center justify-between mt-3 text-sm text-muted-foreground">
                        <span>
                          Showing {sourceFilesPage * SOURCE_FILES_PER_PAGE + 1}–{Math.min((sourceFilesPage + 1) * SOURCE_FILES_PER_PAGE, suspectedSourceFiles.length)} of {suspectedSourceFiles.length}
                        </span>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={sourceFilesPage === 0}
                            onClick={() => setSourceFilesPage(p => p - 1)}
                          >
                            Previous
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={(sourceFilesPage + 1) * SOURCE_FILES_PER_PAGE >= suspectedSourceFiles.length}
                            onClick={() => setSourceFilesPage(p => p + 1)}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Note about code fixes */}
                {codeFixes.length === 0 && incident.status === 'analyzing_code' && (
                  <Alert className="border-blue-200 bg-blue-50 dark:bg-blue-900/10">
                    <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
                    <AlertDescription className="text-sm">
                      Kestrel is analyzing application code to generate fixes. This may take a minute...
                    </AlertDescription>
                  </Alert>
                )}
                {codeFixes.length === 0 && incident.status !== 'analyzing_code' && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-sm">
                      <div className="flex items-center justify-between gap-2">
                        <span>
                          Kestrel code analysis was attempted but couldn't automatically generate a fix. Review the suspected source files above to manually fix the issue, or retry the analysis.
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={isRetryingCodeAnalysis}
                          onClick={async () => {
                            try {
                              setCodeAnalysisRetryError(null);
                              setIsRetryingCodeAnalysis(true);
                              await apiClient.retryCodeAnalysis(incident.id);
                              // Update local state to show analyzing spinner immediately
                              setIncident({ ...incident, status: 'analyzing_code' });
                              // Poll until analysis completes (status changes from analyzing_code)
                              const poll = async () => {
                                for (let i = 0; i < 30; i++) {
                                  await new Promise(r => setTimeout(r, 5000));
                                  try {
                                    const updated = await apiClient.getIncidentDetails(incident.id);
                                    if (updated.status !== 'analyzing_code') {
                                      setIncident(updated);
                                      return;
                                    }
                                  } catch { /* keep polling */ }
                                }
                                // Timeout — refresh once more
                                const final = await apiClient.getIncidentDetails(incident.id);
                                setIncident(final);
                              };
                              poll();
                            } catch (err) {
                              console.error('Retry failed:', err);
                              setCodeAnalysisRetryError('Failed to start code analysis retry. Please try again.');
                            } finally {
                              setIsRetryingCodeAnalysis(false);
                            }
                          }}
                        >
                          {isRetryingCodeAnalysis ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RotateCcw className="h-3 w-3 mr-1" />}
                          Retry
                        </Button>
                      </div>
                      {codeAnalysisRetryError && (
                        <p className="text-red-600 dark:text-red-400 text-xs mt-1">{codeAnalysisRetryError}</p>
                      )}
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}

          {/* Causal Pull Requests - PRs/MRs that may have introduced the breaking change */}
          {hasRCA && isApplicationLevelFailure && (causalPRs.length > 0 || incident.rca_result?.causal_pr_status === 'searching') && (
            <Card>
              <CardHeader>
                <CardTitle>
                  Causal Pull Requests {causalPRs.length > 0 && `(${causalPRs.length})`}
                </CardTitle>
                <CardDescription className="mt-1">
                  Recently merged pull requests that may have introduced the breaking change
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {causalPRs.length === 0 && incident.rca_result?.causal_pr_status === 'searching' && (
                  <Alert>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <AlertDescription className="text-sm">
                      Searching for pull requests that may have caused this incident...
                    </AlertDescription>
                  </Alert>
                )}
                {causalPRs.map((pr) => (
                  <div key={pr.url} className="border rounded-lg p-4 space-y-2 overflow-hidden">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <a
                          href={/^https?:\/\//i.test(pr.url) ? pr.url : '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1.5 break-words"
                        >
                          {pr.change_type === 'commit'
                            ? <><span className="font-mono">{pr.commit_sha?.slice(0, 7)}</span>: {pr.title}</>
                            : <>{pr.provider === 'gitlab' ? '!' : '#'}{pr.number}: {pr.title}</>
                          }
                          <ExternalLink className="h-3 w-3 flex-shrink-0" />
                        </a>
                        <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-muted-foreground">
                          <span>by {pr.author}</span>
                          <span>{pr.change_type === 'commit' ? 'committed' : 'merged'} {pr.merged_at ? new Date(pr.merged_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC', timeZoneName: 'short' }) : 'N/A'}</span>
                          <span className="text-muted-foreground/70">{pr.repo_name}</span>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-xs flex-shrink-0">
                        {pr.provider === 'gitlab' ? 'GitLab' : 'GitHub'}
                      </Badge>
                    </div>
                    {pr.files_changed && pr.files_changed.length > 0 && (
                      <Collapsible>
                        <CollapsibleTrigger className="group flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                          <ChevronRight className="h-3 w-3 transition-transform group-data-[state=open]:rotate-90" />
                          {pr.files_changed.length} file{pr.files_changed.length !== 1 ? 's' : ''} changed
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="mt-1 pl-4 space-y-0.5">
                            {pr.files_changed.map((file, i) => (
                              <div key={i} className="text-xs text-muted-foreground font-mono truncate">{file}</div>
                            ))}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                    {pr.relevance_explanation && (
                      <Collapsible>
                        <CollapsibleTrigger className="group flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                          <ChevronRight className="h-3 w-3 transition-transform group-data-[state=open]:rotate-90" />
                          Why this PR is relevant
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <p className="mt-1 text-sm text-muted-foreground bg-muted/50 rounded p-2">
                            {pr.relevance_explanation}
                          </p>
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Code Fixes for Application-Level Incidents */}
          {hasRCA && codeFixes.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div>
                    <CardTitle>
                      Code Fixes ({codeFixes.length})
                    </CardTitle>
                    <CardDescription className="mt-1">
                      Application code changes to fix the underlying bug
                    </CardDescription>
                  </div>
                  {codeFixes.some(f => f.repo_id) && (
                    codeFixPrUrl ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(codeFixPrUrl, '_blank')}
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View Pull Request
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={creatingCodeFixPR}
                        onClick={async () => {
                          // Group fixes by repo_id to avoid sending cross-repo fixes to the wrong repo
                          const fixesByRepo = new Map<string, typeof codeFixes>();
                          for (const fix of codeFixes) {
                            if (!fix.repo_id) continue;
                            const existing = fixesByRepo.get(fix.repo_id) || [];
                            existing.push(fix);
                            fixesByRepo.set(fix.repo_id, existing);
                          }
                          if (fixesByRepo.size === 0) return;

                          setCreatingCodeFixPR(true);
                          setCodeFixPrError(null);
                          const codeProvider = incident.rca_result?.code_provider || 'github';
                          const isGitLab = codeProvider === 'gitlab';
                          let lastPrUrl: string | null = null;
                          try {
                            // Create a PR/MR for each repo
                            for (const [repoId, repoFixes] of Array.from(fixesByRepo.entries())) {
                              const requestData = {
                                incident_id: incident.id,
                                repository_id: repoId,
                                fixes: repoFixes.map(f => ({ file_path: f.file_path, fixed_content: f.fixed_code, original_content: f.original_code || '', line_start: f.line_start || 0, line_end: f.line_end || 0 })),
                                pr_title: `[KestrelAI] Code Fix: ${repoFixes.length} file(s) for ${incident.rca_result?.incident_type || 'incident'}`,
                                pr_description: `Automated code fix for [incident](${window.location.origin}/incidents/${incident.id}).\n\nFiles changed:\n${repoFixes.map(f => `- ${f.file_path}: ${f.explanation}`).join('\n')}`,
                              };
                              if (isGitLab) {
                                const result = await apiClient.createCodeFixMergeRequest(requestData);
                                if (result.success && result.mr_url) {
                                  lastPrUrl = result.mr_url;
                                }
                              } else {
                                const result = await apiClient.createCodeFixPullRequest(requestData);
                                if (result.success && result.pr_url) {
                                  lastPrUrl = result.pr_url;
                                }
                              }
                            }
                            // Open the last created PR/MR URL (only one window.open per user gesture to avoid popup blockers)
                            if (lastPrUrl) {
                              window.open(lastPrUrl, '_blank', 'noopener,noreferrer');
                            }
                          } catch (err: any) {
                            const errorMsg = typeof err.response?.data === 'string'
                              ? err.response.data
                              : err.response?.data?.message || err.message || `Failed to create ${isGitLab ? 'MR' : 'PR'}`;
                            setCodeFixPrError(errorMsg);
                          } finally {
                            if (lastPrUrl) {
                              setCodeFixPrUrl(lastPrUrl);
                            }
                            setCreatingCodeFixPR(false);
                          }
                        }}
                      >
                        {creatingCodeFixPR ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <GitPullRequest className="h-4 w-4 mr-2" />
                        )}
                        {creatingCodeFixPR ? 'Creating...' : `Create ${(incident.rca_result?.code_provider === 'gitlab') ? 'MR' : 'PR'} for All ${codeFixes.length} Fixes`}
                      </Button>
                    )
                  )}
                </div>
                {codeFixPrError && (
                  <div className="text-sm text-destructive mt-2">{codeFixPrError}</div>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                {codeFixes.map((fix, index) => (
                  <CodeFixCard
                    key={index}
                    fix={fix}
                    index={index}
                    incidentId={incident.id}
                    prUrl={codeFixPrUrl || undefined}
                  />
                ))}
              </CardContent>
            </Card>
          )}

          {/* Post-Mortem Analysis */}
          {postMortemData && (
            <Card>
              <CardHeader>
                <CardTitle>
                  Post-Mortem Analysis
                </CardTitle>
                <CardDescription>
                  AI-generated incident review with lessons learned and recommendations
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {postMortemData.postmortem_summary && (
                  <p className="text-sm leading-relaxed border-l-2 border-muted-foreground/30 pl-4 text-muted-foreground italic">
                    {postMortemData.postmortem_summary}
                  </p>
                )}

                {postMortemData.what_went_well && postMortemData.what_went_well.length > 0 && (
                  <div className="p-4 rounded-md border">
                    <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      What Went Well
                    </h3>
                    <ul className="space-y-1.5">
                      {postMortemData.what_went_well.map((item: string, idx: number) => (
                        <li key={idx} className="text-sm text-foreground pl-2">• {item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {postMortemData.what_could_improve && postMortemData.what_could_improve.length > 0 && (
                  <div className="p-4 rounded-md border">
                    <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-orange-500" />
                      What Could Improve
                    </h3>
                    <ul className="space-y-1.5">
                      {postMortemData.what_could_improve.map((item: string, idx: number) => (
                        <li key={idx} className="text-sm text-foreground pl-2">• {item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {postMortemData.lessons_learned && postMortemData.lessons_learned.length > 0 && (
                  <div className="p-4 rounded-md border">
                    <h3 className="text-sm font-semibold text-foreground mb-3">Lessons Learned</h3>
                    <div className="space-y-2">
                      {postMortemData.lessons_learned.map((lesson: any, idx: number) => (
                        <div key={idx} className="p-3 rounded-md border text-sm">
                          <div className="flex items-center gap-2 mb-1.5">
                            <Badge variant="outline" className="text-xs">{lesson.severity} priority</Badge>
                            <span className="text-xs text-muted-foreground">{lesson.category?.replace(/_/g, ' ')}</span>
                          </div>
                          <p>{lesson.lesson}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {postMortemData.prevention_recommendations && postMortemData.prevention_recommendations.length > 0 && (
                  <div className="p-4 rounded-md border">
                    <h3 className="text-sm font-semibold text-foreground mb-3">Prevention Recommendations</h3>
                    <div className="space-y-2">
                      {postMortemData.prevention_recommendations.map((rec: any, idx: number) => (
                        <div key={idx} className="p-3 rounded-md border text-sm">
                          <div className="flex items-center gap-2 mb-1.5">
                            <Badge variant="outline" className="text-xs">{rec.priority} priority</Badge>
                            <span className="text-xs text-muted-foreground">{rec.effort} effort · {rec.category?.replace(/_/g, ' ')}</span>
                          </div>
                          <p>{rec.recommendation}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {postMortemData.similar_incidents_to_monitor && postMortemData.similar_incidents_to_monitor.length > 0 && (
                  <div className="p-4 rounded-md border">
                    <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Eye className="h-4 w-4 text-muted-foreground" />
                      Similar Incidents to Monitor
                    </h3>
                    <ul className="space-y-1.5">
                      {postMortemData.similar_incidents_to_monitor.map((pattern: string, idx: number) => (
                        <li key={idx} className="text-sm text-foreground pl-2">• {pattern}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {postMortemData.impact_assessment && (
                  <div className="p-4 rounded-md border">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-foreground">Impact Assessment</h3>
                      {!editingImpact && (
                        <Button variant="outline" size="sm" onClick={() => setEditingImpact(true)}>
                          <Edit className="h-3 w-3 mr-1" /> Edit
                        </Button>
                      )}
                    </div>
                    {editingImpact ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="user_impact" className="text-xs text-muted-foreground">User Impact</Label>
                            <Input id="user_impact" value={impactFormData.user_impact} onChange={(e) => setImpactFormData({...impactFormData, user_impact: e.target.value})} className="mt-1" />
                          </div>
                          <div>
                            <Label htmlFor="duration" className="text-xs text-muted-foreground">Duration (minutes)</Label>
                            <Input id="duration" type="number" value={impactFormData.duration_minutes} onChange={(e) => setImpactFormData({...impactFormData, duration_minutes: parseInt(e.target.value) || 0})} className="mt-1" />
                          </div>
                          <div>
                            <Label htmlFor="affected_users" className="text-xs text-muted-foreground">Affected Users</Label>
                            <Input id="affected_users" value={impactFormData.affected_users} onChange={(e) => setImpactFormData({...impactFormData, affected_users: e.target.value})} className="mt-1" />
                          </div>
                          <div>
                            <Label htmlFor="business_impact" className="text-xs text-muted-foreground">Business Impact</Label>
                            <Input id="business_impact" value={impactFormData.business_impact} onChange={(e) => setImpactFormData({...impactFormData, business_impact: e.target.value})} className="mt-1" />
                          </div>
                          <div className="col-span-2">
                            <Label htmlFor="technical_impact" className="text-xs text-muted-foreground">Technical Impact</Label>
                            <Input id="technical_impact" value={impactFormData.technical_impact} onChange={(e) => setImpactFormData({...impactFormData, technical_impact: e.target.value})} className="mt-1" />
                          </div>
                        </div>
                        <div className="flex gap-2 pt-2">
                          <Button variant="default" size="sm" onClick={handleSaveImpactAssessment} disabled={savingImpact} className="bg-green-600 hover:bg-green-700">
                            {savingImpact ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Saving...</> : <><Save className="h-3 w-3 mr-1" />Save</>}
                          </Button>
                          <Button variant="outline" size="sm" onClick={handleCancelImpactEdit} disabled={savingImpact}>
                            <X className="h-3 w-3 mr-1" /> Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="p-2 rounded-md bg-muted">
                          <span className="text-muted-foreground block text-xs mb-0.5">User Impact</span>
                          <span className="font-medium">{postMortemData.impact_assessment.user_impact}</span>
                        </div>
                        <div className="p-2 rounded-md bg-muted">
                          <span className="text-muted-foreground block text-xs mb-0.5">Duration</span>
                          <span className="font-medium">{postMortemData.impact_assessment.duration_minutes} minutes</span>
                        </div>
                        <div className="p-2 rounded-md bg-muted">
                          <span className="text-muted-foreground block text-xs mb-0.5">Affected Users</span>
                          <span className="font-medium">{postMortemData.impact_assessment.affected_users}</span>
                        </div>
                        <div className="p-2 rounded-md bg-muted">
                          <span className="text-muted-foreground block text-xs mb-0.5">Business Impact</span>
                          <span className="font-medium">{postMortemData.impact_assessment.business_impact}</span>
                        </div>
                        <div className="col-span-2 p-2 rounded-md bg-muted">
                          <span className="text-muted-foreground block text-xs mb-0.5">Technical Impact</span>
                          <span className="font-medium">{postMortemData.impact_assessment.technical_impact}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  {postMortemData.detection_effectiveness && (
                    <div className="p-4 rounded-md border">
                      <h3 className="text-sm font-semibold text-foreground mb-3">Detection Effectiveness</h3>
                      <div className="space-y-2 text-sm">
                        <div><span className="text-muted-foreground">Time to Detect:</span> <span className="font-medium">{postMortemData.detection_effectiveness.time_to_detect}</span></div>
                        <div><span className="text-muted-foreground">Method:</span> <span className="font-medium">{postMortemData.detection_effectiveness.detection_method}</span></div>
                        <div><span className="text-muted-foreground">Rating:</span> <Badge variant="outline" className="ml-2 text-xs">{postMortemData.detection_effectiveness.effectiveness_rating}</Badge></div>
                        {postMortemData.detection_effectiveness.improvements && (
                          <div className="pt-2 border-t"><span className="text-muted-foreground text-xs">Improvements:</span><p className="text-xs mt-1">{postMortemData.detection_effectiveness.improvements}</p></div>
                        )}
                      </div>
                    </div>
                  )}
                  {postMortemData.remediation_effectiveness && (
                    <div className="p-4 rounded-md border">
                      <h3 className="text-sm font-semibold text-foreground mb-3">Remediation Effectiveness</h3>
                      <div className="space-y-2 text-sm">
                        <div><span className="text-muted-foreground">Time to Resolve:</span> <span className="font-medium">{postMortemData.remediation_effectiveness.time_to_resolve}</span></div>
                        <div><span className="text-muted-foreground">Fix Success:</span> <span className="font-medium">{postMortemData.remediation_effectiveness.fix_success}</span></div>
                        <div><span className="text-muted-foreground">Rating:</span> <Badge variant="outline" className="ml-2 text-xs">{postMortemData.remediation_effectiveness.effectiveness_rating}</Badge></div>
                        {postMortemData.remediation_effectiveness.improvements && (
                          <div className="pt-2 border-t"><span className="text-muted-foreground text-xs">Improvements:</span><p className="text-xs mt-1">{postMortemData.remediation_effectiveness.improvements}</p></div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Incident History (previous occurrences of the same issue) */}
          {previousIncidents.length > 0 && incident && (
            <Card>
              <CardHeader>
                <CardTitle>
                  Incident History
                </CardTitle>
                <CardDescription>
                  {previousIncidents.length} previous occurrence{previousIncidents.length !== 1 ? 's' : ''} of {incident.type} for {incident.signals?.[0]?.namespace ? `${incident.signals[0].namespace}/` : ''}{incident.signals?.[0]?.resource_name || 'this resource'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="divide-y divide-border rounded-lg border overflow-hidden">
                  {previousIncidents
                    .slice(historyPage * HISTORY_PER_PAGE, (historyPage + 1) * HISTORY_PER_PAGE)
                    .map((prev) => {
                      const displayType = getIncidentTypeLabel(prev.incident_type || prev.type);
                      const yamlCount = prev.yaml_fix_count || 0;
                      const codeCount = prev.code_fix_count || 0;
                      return (
                        <Link
                          key={prev.id}
                          to={`/incidents/${prev.id}`}
                          className="flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={cn(
                              "w-2 h-2 rounded-full flex-shrink-0",
                              prev.status === 'remediation_applied' || prev.status === 'resolved_confirmed'
                                ? "bg-green-500" : "bg-muted-foreground"
                            )} />
                            <div className="min-w-0">
                              <p className="text-sm font-medium">{displayType}</p>
                              <p className="text-xs text-muted-foreground">
                                {formatDateTime(prev.detected_at)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-xs text-muted-foreground capitalize">
                              {prev.status === 'remediation_applied' ? 'Remediated' :
                               prev.status === 'resolved_confirmed' ? 'Resolved' :
                               prev.status === 'resolved' ? 'Analyzed' :
                               prev.status}
                            </span>
                            {(yamlCount > 0 || codeCount > 0) && (
                              <Badge variant="secondary" className="text-xs font-normal">
                                {yamlCount > 0 && `${yamlCount} YAML fix${yamlCount !== 1 ? 'es' : ''}`}
                                {yamlCount > 0 && codeCount > 0 && ', '}
                                {codeCount > 0 && `${codeCount} code fix${codeCount !== 1 ? 'es' : ''}`}
                              </Badge>
                            )}
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          </div>
                        </Link>
                      );
                    })}
                </div>

                {/* Pagination */}
                {previousIncidents.length > HISTORY_PER_PAGE && (
                  <div className="flex items-center justify-between mt-4 text-sm text-muted-foreground">
                    <span>
                      Showing {historyPage * HISTORY_PER_PAGE + 1}–{Math.min((historyPage + 1) * HISTORY_PER_PAGE, previousIncidents.length)} of {previousIncidents.length}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={historyPage === 0}
                        onClick={() => setHistoryPage(p => p - 1)}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={(historyPage + 1) * HISTORY_PER_PAGE >= previousIncidents.length}
                        onClick={() => setHistoryPage(p => p + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Metadata */}
        <div className="space-y-6">
          {/* Incident Metadata */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Incident Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3 text-sm">
                <div>
                  <span className="text-muted-foreground">Detected</span>
                  <div className="font-medium mt-1">
                    {formatDateTime(incident.detected_at)}
                  </div>
                </div>

                {incident.resolved_at && (
                  <div>
                    <span className="text-muted-foreground">Analysis Completed</span>
                    <div className="font-medium mt-1">
                      {formatDateTime(incident.resolved_at)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Duration: {Math.round((incident.resolved_at - incident.detected_at) / 60)} minutes
                    </div>
                  </div>
                )}

                {incident.remediation_applied && incident.remediation_applied_at && (
                  <div>
                    <span className="text-muted-foreground">Remediation Applied</span>
                    <div className="font-medium mt-1 text-green-600">
                      {formatDateTime(incident.remediation_applied_at)}
                    </div>
                    {incident.remediation_applied_by && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Applied by: {incident.remediation_applied_by}
                      </div>
                    )}
                    {incident.remediation_fixes_count > 0 && (
                      <div className="text-xs text-muted-foreground mt-1">
                        {incident.remediation_fixes_count} fix{incident.remediation_fixes_count !== 1 ? 'es' : ''} applied
                      </div>
                    )}
                  </div>
                )}

                <Separator />

                <div>
                  <span className="text-muted-foreground">Cluster</span>
                  <div className="font-medium mt-1">{incident.cluster_name}</div>
                </div>

                <div>
                  <span className="text-muted-foreground">Type</span>
                  <div className="font-medium mt-1">
                    {incident.rca_result?.incident_type && incident.rca_result.incident_type !== 'Unknown' && incident.rca_result.incident_type !== 'Failed'
                      ? getIncidentTypeLabel(incident.rca_result.incident_type)
                      : getIncidentTypeLabel(incident.type)}
                  </div>
                </div>

                <div>
                  <span className="text-muted-foreground">Severity</span>
                  <div className="mt-1">
                    <Badge variant={incident.severity === 'critical' ? 'destructive' : 'secondary'}>
                      {incident.severity}
                    </Badge>
                  </div>
                </div>

                <div>
                  <span className="text-muted-foreground">Status</span>
                  <div className="mt-1">
                    <Badge variant={incident.status === 'resolved' || incident.status === 'remediation_applied' || incident.status === 'resolved_confirmed' ? 'outline' : 'default'}>
                      {getStatusLabel(incident.status)}
                    </Badge>
                  </div>
                </div>

                {/* Namespace Owners */}
                {incident.namespace_owner_emails && incident.namespace_owner_emails.length > 0 && (
                  <div>
                    <span className="text-muted-foreground">Namespace Owners</span>
                    <TooltipProvider>
                      <div className="flex items-center gap-2 mt-2">
                        {incident.namespace_owner_emails.map((email) => (
                          <Tooltip key={email}>
                            <TooltipTrigger asChild>
                              <div
                                className="relative w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-xs bg-gradient-to-br from-blue-500 to-blue-600 border-2 border-background shadow-md cursor-help"
                              >
                                {getInitials(email)}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="text-xs">{email}</p>
                            </TooltipContent>
                          </Tooltip>
                        ))}
                      </div>
                    </TooltipProvider>
                  </div>
                )}

                {/* Incident Actions - only show if not in final resolved state */}
                {incident.status !== 'resolved_confirmed' && (
                  <>
                    <Separator />

                    <div className="space-y-3">
                      <span className="text-sm font-semibold">Actions</span>
                      
                      {/* Validate Fix button - for incidents with analysis complete or remediation applied */}
                      {(incident.status === 'resolved' || incident.status === 'remediation_applied') && (
                        <div className="space-y-2">
                      <Button
                        onClick={handleValidateFix}
                        disabled={validating}
                        variant="default"
                        className="w-full"
                        size="sm"
                      >
                        {validating ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Validating Fix...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Validate Fix
                          </>
                        )}
                      </Button>
                      <p className="text-xs text-muted-foreground">
                        AI agents will verify if the incident has been resolved by checking kubernetes events, logs, and pod statuses. Use this after applying the fix (via Apply button, kubectl, or GitOps).
                      </p>
                      
                      {validationResult && (
                        <Alert className={
                          validationResult.validation_status === 'fixed' 
                            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                            : validationResult.validation_status === 'recurring'
                            ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                            : validationResult.validation_status === 'not_fixed'
                            ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800'
                            : validationResult.validation_status === 'in_progress'
                            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                            : validationResult.validation_status === 'error'
                            ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                            : ''
                        }>
                          <AlertDescription>
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                {validationResult.validation_status === 'fixed' ? (
                                  <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                                ) : validationResult.validation_status === 'in_progress' ? (
                                  <Loader2 className="h-4 w-4 text-blue-600 dark:text-blue-400 animate-spin" />
                                ) : validationResult.validation_status === 'error' ? (
                                  <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                                ) : (
                                  <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                                )}
                                <strong className="capitalize">{validationResult.validation_status.replace('_', ' ')}</strong>
                              </div>
                              <p className="text-sm">{validationResult.validation_summary}</p>
                              {validationResult.validation_details?.length > 0 && (
                                <ul className="text-xs mt-2 list-disc list-inside space-y-1">
                                  {validationResult.validation_details.map((detail: string, idx: number) => (
                                    <li key={idx}>{detail}</li>
                                  ))}
                                </ul>
                              )}
                              {validationResult.checked_at && validationResult.validation_status !== 'in_progress' && (
                                <p className="text-xs text-muted-foreground mt-2">
                                  Checked: {new Date(validationResult.checked_at).toLocaleString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit',
                                    timeZone: 'UTC',
                                    timeZoneName: 'short',
                                  })}
                                </p>
                              )}
                            </div>
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  )}
                  
                  {/* Mark as Resolved button - always show */}
                  <div className="space-y-2">
                    <Button
                      onClick={() => setShowResolveDialog(true)}
                      disabled={markingResolved}
                      variant="outline"
                      className="w-full"
                      size="sm"
                    >
                      {markingResolved ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Marking Resolved...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Mark as Resolved
                        </>
                      )}
                    </Button>
                  </div>
                    </div>
                  </>
                )}

              </div>
            </CardContent>
          </Card>

          {/* Affected Resources */}
          {hasRCA && affectedResources.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Affected Resources</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {affectedResources.map((resource, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-2 p-2 rounded-md bg-muted/50 text-sm"
                    >
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="font-mono text-xs break-all">
                          {resource.namespace && `${resource.namespace}/`}
                          {resource.name}
                        </div>
                        {(resource.type || resource.kind || resource.resource_type) && (
                          <div className="text-xs text-muted-foreground">{resource.type || resource.kind || resource.resource_type}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Signals */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Detection Signals ({signals.length})</CardTitle>
              <CardDescription className="text-xs">
                Kubernetes events and status changes that triggered this incident
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {signals.map((signal, index) => (
                  <div
                    key={index}
                    className="p-3 rounded-md border bg-card text-xs space-y-1"
                  >
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="text-xs font-mono">
                        {signal.signal_type}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {formatTime(signal.timestamp)}
                      </span>
                    </div>
                    <div className="font-medium">{signal.reason}</div>
                    <div className="text-muted-foreground line-clamp-2">{signal.message}</div>
                    {(() => {
                      const meta = typeof signal.metadata === 'string'
                        ? (() => { try { return JSON.parse(signal.metadata); } catch { return null; } })()
                        : signal.metadata;
                      return meta?.pagerduty_url ? (
                        <a
                          href={meta.pagerduty_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-500 hover:text-blue-400 mt-1"
                        >
                          <ExternalLink className="h-3 w-3" />
                          View in PagerDuty
                        </a>
                      ) : null;
                    })()}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Mark as Resolved Confirmation Dialog */}
      <AlertDialog open={showResolveDialog} onOpenChange={setShowResolveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark Incident as Resolved?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              {incident.status === 'remediation_applied' ? (
                <>
                  <p>This will close the incident.</p>
                  {!incident.validation_status && (
                    <p className="text-amber-600 dark:text-amber-400 font-medium">
                      Consider using "Validate Fix" first to confirm the remediation actually worked.
                    </p>
                  )}
                  {incident.validation_status && incident.validation_status !== 'fixed' && (
                    <p className="text-amber-600 dark:text-amber-400 font-medium">
                      ⚠️ Validation status: {incident.validation_status}. The fix may not be working correctly.
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p>Use this if you fixed the issue manually, or if it's a false positive.</p>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmMarkResolved} disabled={markingResolved}>
              {markingResolved ? 'Marking Resolved...' : 'Mark as Resolved'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
        </div>
      </div>

      {/* Chat Interface - Right Side */}
      {chatMounted && (
        <div className={cn(
          "w-full max-h-[50vh] md:max-h-none md:w-[31%] border-t md:border-t-0 md:border-l border-border/50 bg-card/30 min-w-0 overflow-hidden",
          !showChat && "hidden"
        )}>
          <IncidentChat incident={incident} />
        </div>
      )}
      </div>
    </div>
  );
}

